import type { ZoneLetter } from "../api/types";
import { t } from "../i18n/i18n";

/** GDH doctrinal « 051200B OCT 26 », comme le serveur (generex_engine.dtg) et l'iPad (formattedDate) : toutes
 *  les lettres OTAN à décalage fixe (registre K16) — A à I et K à M = UTC+1 à UTC+12, N à Y = UTC−1 à UTC−12,
 *  Z = UTC, J exclu (heure locale de l'observateur) ; Alpha, Bravo et Zoulou restent ceux de la DIREX de l'iPad.
 *  Une date sans fuseau est l'heure locale du fuseau choisi. Sert à recalculer le GDH d'une ligne pendant son
 *  glissement. */

/** Lettres OTAN dans l'ordre des décalages : à l'est A (+1) à M (+12) sans J, à l'ouest N (−1) à Y (−12). */
const EAST = "ABCDEFGHIKLM";
const WEST = "NOPQRSTUVWXY";

/** Décalage de chaque fuseau en heures (`generex_engine.dtg.OFFSETS`). */
export const ZONE_OFFSET_H: Readonly<Record<ZoneLetter, number>> = Object.freeze(
  Object.fromEntries([
    ...[...EAST].map((letter, i) => [letter, i + 1]),
    ["Z", 0],
    ...[...WEST].map((letter, i) => [letter, -(i + 1)]),
  ]) as Record<ZoneLetter, number>,
);

/** Les 25 lettres, de l'est (A, UTC+1) à l'ouest (Y, UTC−12), Z entre les deux (ordre de `OFFSETS`). */
export const ZONE_LETTERS: readonly ZoneLetter[] = Object.keys(ZONE_OFFSET_H) as ZoneLetter[];

/** Fuseaux OTAN de l'ouest (Y, UTC−12) à l'est (M, UTC+12), Zoulou entre les deux (registre K16). */
export const ZONES_WEST_TO_EAST: readonly ZoneLetter[] = [...ZONE_LETTERS].sort((a, b) => ZONE_OFFSET_H[a] - ZONE_OFFSET_H[b]);
/** Alphabet phonétique OTAN (identique en français et en anglais, sauf Zoulou, traduit). */
const PHONETIC: Readonly<Record<Exclude<ZoneLetter, "Z">, string>> = {
  A: "Alpha", B: "Bravo", C: "Charlie", D: "Delta", E: "Echo", F: "Foxtrot", G: "Golf", H: "Hotel", I: "India",
  K: "Kilo", L: "Lima", M: "Mike", N: "November", O: "Oscar", P: "Papa", Q: "Quebec", R: "Romeo", S: "Sierra",
  T: "Tango", U: "Uniform", V: "Victor", W: "Whiskey", X: "X-ray", Y: "Yankee",
};

/** « C (UTC+3) · Charlie », « Z (UTC) · Zoulou », « N (UTC−1) · November » : la lettre d'abord, celle des GDH de
 *  l'ordre (« 051200C OCT 26 »), puis le décalage et le nom phonétique (registre K16, les 25 lettres OTAN). */
export function zoneLabel(letter: ZoneLetter): string {
  const hours = ZONE_OFFSET_H[letter];
  const offset = hours === 0 ? "" : hours > 0 ? `+${hours}` : `−${-hours}`;
  return t("{lettre} (UTC{heures}) · {nom}", { lettre: letter, nom: letter === "Z" ? t("Zoulou") : PHONETIC[letter], heures: offset });
}

const own = (zone: string) => Object.prototype.hasOwnProperty.call(ZONE_OFFSET_H, zone);

/** Lettre OTAN connue (« J », une lettre minuscule ou un nom de fuseau ne le sont pas). */
export function isZoneLetter(zone: unknown): zone is ZoneLetter {
  return typeof zone === "string" && own(zone);
}
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

const pad = (n: number) => String(n).padStart(2, "0");

export function formatDtg(start: string, hours: number, zone: string | null): string | null {
  const z: ZoneLetter = isZoneLetter(zone) ? zone : "Z";
  const offset = ZONE_OFFSET_H[z];
  const naive = NAIVE.exec(start);
  let epoch: number;
  if (naive) {
    // Secondes facultatives : `Number(undefined)` vaut NaN (et non undefined), d'où `|| 0`.
    const [, y, mo, d, h, mi, s] = naive.map(Number) as number[];
    epoch = Date.UTC(y!, mo! - 1, d!, h!, mi!, s || 0) - offset * 3_600_000;
  } else {
    epoch = Date.parse(start);
    if (Number.isNaN(epoch)) return null;
  }
  const wall = new Date(epoch + (offset + hours) * 3_600_000);
  return (
    `${pad(wall.getUTCDate())}${pad(wall.getUTCHours())}${pad(wall.getUTCMinutes())}${z} ` +
    `${MONTHS[wall.getUTCMonth()]} ${pad(wall.getUTCFullYear() % 100)}`
  );
}

/** Décalage du fuseau en heures (0 : Zoulou, lettre inconnue ou absente, comme `dtg.offset_hours`). */
export function zoneOffsetH(zone: string | null): number {
  return isZoneLetter(zone) ? ZONE_OFFSET_H[zone] : 0;
}

/** Valeur d'un champ « date et heure » (AAAA-MM-JJTHH:MM) lue dans le fuseau de l'exercice. */
export function toZoneInput(iso: string, zone: string | null): string {
  const epoch = Date.parse(iso);
  if (Number.isNaN(epoch)) return "";
  return new Date(epoch + zoneOffsetH(zone) * 3_600_000).toISOString().slice(0, 16);
}

/** Instant (ISO, UTC) d'une saisie « date et heure » faite dans le fuseau de l'exercice. */
export function fromZoneInput(value: string, zone: string | null): string | null {
  const naive = NAIVE.exec(value);
  if (!naive) return null;
  const [, y, mo, d, h, mi] = naive.map(Number) as number[];
  return new Date(Date.UTC(y!, mo! - 1, d!, h!, mi!) - zoneOffsetH(zone) * 3_600_000).toISOString();
}
